// DayViewTimelineService.js
// ===============================================================
// Single-day grid: hours across (sticky header) + tech rows down.
// ===============================================================

import React, { useMemo, useRef, useEffect } from 'react';

/* ---------- helpers ---------- */
const COLORS     = ['#fecaca','#fde68a','#bbf7d0','#bfdbfe','#e9d5ff',
                    '#fbcfe8','#fed7aa','#e2e8f0'];
const colorFor   = id => COLORS[Math.abs(id) % COLORS.length];
const toFloat    = (t='00:00') => { const [h,m] = t.split(':'); return +h + +m/60; };
const hourLabel  = h => `${h%12||12}:00 ${h<12?'AM':'PM'}`;

/* tiny 4-px ribbon on the left edge */
const ribbonClass = status =>
  status === 'COMPLETED'   ? 'border-l-4 border-green-600'
: status === 'INCOMPLETE'  ? 'border-l-4 border-red-600'
: status === 'ACTIVATED'   ? 'border-l-4 border-blue-600'
:                            '';

/* =============================================================== */
export default function DayViewTimelineService({
  dayDate,
  calls            = [],
  employees        = [],
  selectedTechs    = [],
  departmentFilter = '',
  onEmptySlot,                     // (techId, hr, min)
  onEventClick,                    // (call)
}) {
  /* ------- layout consts ------- */
  const COL_W  = 80;               // px / hour
  const ROW_H  = 70;               // px / tech
  const COLS   = 24;
  const GRID_W = COL_W * COLS;

  /* ------- container ref to auto-scroll ------- */
  const containerRef = useRef(null);
  useEffect(() => {
    containerRef.current && (containerRef.current.scrollLeft = 7 * COL_W);   // 7 AM
  }, []);

  /* ------- compute tech rows (sorted) ------- */
  const techRows = useMemo(() => (
    [...employees]
      .filter(t => !departmentFilter || t.department === departmentFilter)
      .sort((a,b) =>
          a.department !== b.department
            ? a.department.localeCompare(b.department)
            : a.first_name .localeCompare(b.first_name))
  ), [employees, departmentFilter]);

  /* ------- bucket calls by tech full-name ------- */
  // const byTech = {};
  // calls.forEach(c => {
  //   if (selectedTechs.length && !selectedTechs.includes(c.TECH)) return;
  //   (byTech[c.TECH] ||= []).push(c);
  // });
    const byTech = {};
    calls.forEach(c => {
      // Lead tech (by id if available, else name)
      if (c.tech_id) {
        (byTech[c.tech_id] ||= []).push(c);
      } else if (c.TECH) {
        (byTech[c.TECH] ||= []).push(c);
      }
      // Helper tech(s)
      if (Array.isArray(c.add_tech)) {
        c.add_tech.forEach(helper => {
          (byTech[helper.tech_id] ||= []).push(c);
        });
      }
      return byTech;
    });


  /* ============================================================= */
  return (
    <div
      ref={containerRef}
      className="border rounded overflow-x-auto"
      style={{ maxHeight:'calc(100vh - 200px)', overflowY:'auto' }}
    >

      {/* ── sticky hour header row ─────────────────────────── */}
      <div
        className="sticky top-0 z-15 grid"
        style={{ gridTemplateColumns:`200px ${GRID_W}px` }}
      >
        {/* frozen corner */}
        <div className="bg-gray-100 border-r border-b h-10" />
        {/* hour labels */}
        <div
          className="grid bg-gray-100 border-b h-10"
          style={{ gridTemplateColumns:`repeat(${COLS}, ${COL_W}px)` }}
        >
          {Array.from({length:COLS}).map((_,h)=>(
            <div
              key={h}
              className="border-l text-center text-[11px] font-semibold leading-10"
            >
              {hourLabel(h)}
            </div>
          ))}
        </div>
      </div>

      {/* ── one row per tech ───────────────────────────────── */}
      {techRows.map(tech => {
        const techId = tech.id;
        // const list   = byTech[`${tech.first_name} ${tech.last_name}`] || [];
        const list = byTech[tech.id] || [];


        return (
          <div
            key={techId}
            className="grid border-b"
            style={{
              gridTemplateColumns:`200px ${GRID_W}px`,
              height:ROW_H,
              position:'relative'
            }}
            onClick={e => {
              /* click-empty → emit hour/min for this tech */
              const x = e.nativeEvent.offsetX - 200; if (x < 0) return;
              const hf = x / COL_W;
              onEmptySlot?.(techId, Math.floor(hf), Math.round((hf%1)*60/15)*15);
            }}
          >
            {/* frozen tech name cell */}
            <div className="bg-gray-50 border-r pl-2 flex flex-col justify-center sticky left-0 z-10">
              <span className="font-medium text-sm truncate">
                {tech.first_name} {tech.last_name}
              </span>
          {tech.title && (
          <div className={`text-xs ${tech.title.toLowerCase().includes('helper')  ? 'text-yellow-500' 
                                                                                  : tech.o_f_type === 'Field' 
                                                                                  ? 'text-blue-600'
                                                                                  : 'text-gray-600'
                                                                                }
                                                                                  `}>
                                                                                    {tech.title}
          </div>
          )}            
              <span className="text-xs text-gray-500">{tech.department}</span>
            </div>

            {/* timeline cell */}
            <div className="relative">
              {/* hour guide lines */}
              {Array.from({length:COLS}).map((_,h)=>(
                <div
                  key={h}
                  className="absolute top-0 bottom-0 w-px bg-gray-200"
                  style={{ left:h*COL_W }}
                />
              ))}

              {/* blocks */}
              {list.map((c,i) => {
                const start = toFloat(c.TIME);
                const end   = toFloat(c.end_time || c.TIME) + 1;   // default 1h
                const left  = start * COL_W;
                const width = Math.max((end - start) * COL_W, COL_W / 2);

                /* ribbon bg (diagonal banner) */
                const isCompleted  = (c.STATUS || c.status) === 'COMPLETED';
                const isIncomplete = (c.STATUS || c.status) === 'INCOMPLETE';
                const ribbonBG     = isCompleted  ? '#16a34a'
                                                  : isIncomplete 
                                                  ? '#dc2626'
                                                  : null;

                return (
                  <div
                    key={i}
                    className={`absolute rounded text-[11px] p-1 shadow cursor-pointer 
                              ${c.STATUS === 'tentative' ? 'bg-orange-400 text-orange-600' : ribbonClass(c.STATUS)}`}
                    style={{
                      top:4,bottom:4,left,width:width-4,
                      background:colorFor(techId), overflow:'hidden'
                    }}
                    onClick={e => { e.stopPropagation(); onEventClick(c); }}
                  >
                    {/* diagonal ribbon */}
                    {ribbonBG && (
                      <span
                        style={{
                          position:'absolute', top:-11, left:0,
                          width:110, textAlign:'center',
                          background:ribbonBG, color:'#fff',
                          fontSize:'9px', fontWeight:700,
                          letterSpacing:'0.5px', padding:'2px 0',
                          transform:'rotate(45deg)',
                          transformOrigin:'top left',
                          boxShadow:'0 1px 3px rgba(0,0,0,.35)'
                        }}
                      >
                        {isCompleted ? 'COMPLETED' : 'INCOMPLETE'}
                      </span>
                    )}
                    <div className="font-semibold truncate">{c.CUSTOMER || 'No Cust'}</div>
                    <div className="truncate">{c.TROUBLE}</div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
